import { Trans, useLingui } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconAdd from '@/icons/Add.svg';
import IconTrash from '@/icons/Trash.svg';

import { BrandsMultiSelect } from '../shared/BrandsMultiSelect';
import { LocaleInputs } from '../shared/LocaleInputs';

import { AnswerRow } from './AnswerRow';
import {
  DefectTypeOption,
  QuestionType,
  SubQuestionAnswerInput,
  SubQuestionInput,
  useQuestionTypeOptions,
} from './ConditionFormEditor';
import { getDefaultConfig, QuestionConfigEditor } from './QuestionConfigEditor';

const typeNeedsAnswers = (type: QuestionType) => type !== 'text-and-select';

export const SubQuestionEditor = ({
  subQuestion,
  onUpdate,
  onDelete,
  onAddAnswer,
  onUpdateAnswer,
  onDeleteAnswer,
  defectTypeOptions,
  showErrors,
  questionBrandIds,
}: {
  subQuestion: SubQuestionInput;
  onUpdate: (updates: Partial<SubQuestionInput>) => void;
  onDelete: () => void;
  onAddAnswer: () => void;
  onUpdateAnswer: (subAnswerIndex: number, updates: Partial<SubQuestionAnswerInput>) => void;
  onDeleteAnswer: (subAnswerIndex: number) => void;
  defectTypeOptions: DefectTypeOption[];
  showErrors: boolean;
  questionBrandIds?: string[];
}) => {
  const { t } = useLingui();
  const questionTypeOptions = useQuestionTypeOptions();

  const handleTypeChange = (newType: QuestionType) => {
    const updates: Partial<SubQuestionInput> = { type: newType };

    if (newType !== subQuestion.type) {
      updates.config = getDefaultConfig(newType);

      if (!typeNeedsAnswers(newType)) {
        updates.answers = [];
      }
    }
    onUpdate(updates);
  };

  return (
    <Box>
      <Stack gap="0.75rem">
        <Stack row alignItems="center" justifyContent="space-between">
          <Title size="400" weight="bold">
            <Trans id="settings.condition-forms.sub-question">Sub-question</Trans>
          </Title>
          <Button
            iconOnly
            variant="secondary-danger"
            size="small"
            onPress={onDelete}
            ariaLabel={t({
              id: 'settings.condition-forms.delete-sub-question',
              message: 'Delete sub-question',
            })}
          >
            <IconTrash />
          </Button>
        </Stack>

        <LocaleInputs
          label={t({
            id: 'settings.condition-forms.sub-question-title',
            message: 'Sub-question title',
          })}
          placeholder={t({
            id: 'settings.condition-forms.sub-question-title',
            message: 'Sub-question title',
          })}
          value={subQuestion.title}
          setValue={(title) => onUpdate({ title })}
          showErrors={showErrors}
        />

        <InputSelect
          label={t({
            id: 'settings.condition-forms.sub-question-type',
            message: 'Sub-question type',
          })}
          options={questionTypeOptions}
          value={questionTypeOptions.find((opt) => opt.value === subQuestion.type)}
          getOptionValue={(opt) => opt.value}
          getOptionLabel={(opt) => opt.label}
          onChange={(option) => option && handleTypeChange(option.value)}
        />

        <QuestionConfigEditor
          config={subQuestion.config}
          type={subQuestion.type}
          onConfigChange={(config) => onUpdate({ config })}
          showErrors={showErrors}
        />

        <BrandsMultiSelect
          selectedBrandIds={subQuestion.brands ?? questionBrandIds ?? []}
          setBrandIds={(brandIds) => onUpdate({ brands: brandIds })}
          availableBrandIds={questionBrandIds}
        />

        {typeNeedsAnswers(subQuestion.type) && (
          <SubQuestionAnswersList
            answers={subQuestion.answers}
            onAddAnswer={onAddAnswer}
            onUpdateAnswer={onUpdateAnswer}
            onDeleteAnswer={onDeleteAnswer}
            defectTypeOptions={defectTypeOptions}
            showErrors={showErrors}
          />
        )}
      </Stack>
    </Box>
  );
};

const SubQuestionAnswersList = ({
  answers,
  onAddAnswer,
  onUpdateAnswer,
  onDeleteAnswer,
  defectTypeOptions,
  showErrors,
}: {
  answers: SubQuestionAnswerInput[];
  onAddAnswer: () => void;
  onUpdateAnswer: (subAnswerIndex: number, updates: Partial<SubQuestionAnswerInput>) => void;
  onDeleteAnswer: (subAnswerIndex: number) => void;
  defectTypeOptions: DefectTypeOption[];
  showErrors: boolean;
}) => {
  return (
    <Stack gap="0.5rem">
      <Title size="400" weight="bold">
        <Trans id="settings.condition-forms.sub-question-answers">Sub-question answers</Trans>
      </Title>

      {answers.map((subAnswer, subAnswerIndex) => (
        <SubQuestionAnswerRow
          key={subAnswer.id ?? subAnswerIndex}
          subAnswer={subAnswer}
          subAnswerIndex={subAnswerIndex}
          onUpdate={(updates) => onUpdateAnswer(subAnswerIndex, updates)}
          onDelete={() => onDeleteAnswer(subAnswerIndex)}
          defectTypeOptions={defectTypeOptions}
          showErrors={showErrors}
        />
      ))}

      {answers.length === 0 && (
        <Paragraph size="200" color={showErrors ? 'danger' : 'secondary'}>
          <Trans id="settings.condition-forms.no-sub-question-answers">
            No answers yet. Add at least one answer.
          </Trans>
        </Paragraph>
      )}

      <Button variant="secondary" size="small" onPress={onAddAnswer}>
        <IconAdd />
        <Trans id="settings.condition-forms.add-sub-question-answer">Add answer</Trans>
      </Button>
    </Stack>
  );
};

const SubQuestionAnswerRow = ({
  subAnswer,
  subAnswerIndex,
  onUpdate,
  onDelete,
  defectTypeOptions,
  showErrors,
}: {
  subAnswer: SubQuestionAnswerInput;
  subAnswerIndex: number;
  onUpdate: (updates: Partial<SubQuestionAnswerInput>) => void;
  onDelete: () => void;
  defectTypeOptions: DefectTypeOption[];
  showErrors: boolean;
}) => {
  const { t, i18n } = useLingui();

  const textLabel = t({
    id: 'settings.condition-forms.sub-question-answer-text',
    message: 'Answer text',
  });
  const defectsLabel = t({
    id: 'settings.condition-forms.linked-defects',
    message: 'Linked defect types (optional)',
  });
  const defectsPlaceholder = t({
    id: 'settings.condition-forms.select-defects',
    message: 'Select defect types',
  });

  return (
    <AnswerRow
      header={
        <Paragraph size="200" weight="medium">
          {i18n._({
            id: 'settings.condition-forms.sub-question-answer',
            message: 'Answer {order}',
            values: { order: subAnswerIndex + 1 },
          })}
        </Paragraph>
      }
      value={{ text: subAnswer.text, defects: subAnswer.defects }}
      onUpdate={onUpdate}
      onDelete={onDelete}
      deleteAriaLabel={t({
        id: 'settings.condition-forms.delete-sub-question-answer',
        message: 'Delete answer',
      })}
      textLabel={textLabel}
      textPlaceholder={textLabel}
      defectsLabel={defectsLabel}
      defectsPlaceholder={defectsPlaceholder}
      defectTypeOptions={defectTypeOptions}
      showErrors={showErrors}
    />
  );
};
